import { useMemo, useState } from 'react'
import { Clock, CheckCircle, Loader2, Target } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useOrganization, useUpdateOrganization } from '@/hooks'
import { PageHeader, Forbidden, ComplianceTargetForm } from '@/components'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import type { ComplianceTarget } from '@/types'

const DEFAULT_TIMEZONE = 'America/Toronto'

/**
 * Minimal fallback list for environments where `Intl.supportedValuesOf` is
 * unavailable. Covers the common zones called out in the spec.
 */
const FALLBACK_ZONES: readonly string[] = [
  'UTC',
  'America/Toronto',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Vancouver',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
]

function listTimeZones(): readonly string[] {
  const supported = (
    Intl as unknown as {
      supportedValuesOf?: (key: string) => string[]
    }
  ).supportedValuesOf
  if (typeof supported === 'function') {
    try {
      const zones = supported('timeZone')
      if (Array.isArray(zones) && zones.length > 0) return zones
    } catch {
      // fall through to static list
    }
  }
  return FALLBACK_ZONES
}

/**
 * Format a zone's current offset as "UTC±HH:MM". Uses `longOffset` which
 * returns values like "GMT-04:00"; we normalize the prefix and keep the sign.
 * Returns an empty string if the zone is not recognized.
 */
function formatOffset(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(at)
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value
    if (!raw) return ''
    // raw is typically "GMT-04:00" or "GMT" for UTC.
    if (raw === 'GMT') return 'UTC+00:00'
    return raw.replace(/^GMT/, 'UTC')
  } catch {
    return ''
  }
}

interface TimeZonePickerProps {
  value: string
  onChange: (zone: string) => void
  zones: readonly string[]
}

/**
 * Self-contained searchable combobox. A native Radix Select has no built-in
 * search and 400+ items don't fit an un-filtered list. Exposes an input with
 * `role="combobox"` (by default on text inputs only when explicit, so we set
 * it) and a filtered listbox of `role="option"` entries.
 */
function TimeZonePicker({ value, onChange, zones }: TimeZonePickerProps) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return zones
    return zones.filter((z) => z.toLowerCase().includes(q))
  }, [zones, query])

  const now = new Date()
  const selectedOffset = formatOffset(value, now)

  return (
    <div className="space-y-2">
      {/* The trigger displays the current selection and its offset. It is a
          non-interactive visual; interaction happens through the search input
          + option list below. Marked as a combobox for accessibility / tests. */}
      <div
        role="combobox"
        aria-label="Timezone"
        aria-expanded="true"
        aria-controls="tz-listbox"
        className={cn(
          'flex items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm',
          'shadow-xs'
        )}
      >
        <span className="font-medium">{value}</span>
        {selectedOffset && <span className="text-muted-foreground ml-2">({selectedOffset})</span>}
      </div>
      <Input
        type="search"
        role="searchbox"
        aria-label="Search timezones"
        placeholder="Search timezones (e.g. Toronto, Tokyo, UTC)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul
        id="tz-listbox"
        role="listbox"
        aria-label="Available timezones"
        className="max-h-64 overflow-y-auto rounded-md border"
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">No matching zones</li>
        ) : (
          filtered.map((zone) => {
            const offset = formatOffset(zone, now)
            const isSelected = zone === value
            return (
              <li
                key={zone}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => onChange(zone)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onChange(zone)
                  }
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm',
                  'hover:bg-accent hover:text-accent-foreground',
                  isSelected && 'bg-accent text-accent-foreground font-medium'
                )}
              >
                <span>{zone}</span>
                {offset && <span className="text-muted-foreground">{offset}</span>}
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}

export function OrganizationSettingsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const { data: org, isLoading: orgLoading } = useOrganization(effectiveOrgId)
  const updateMutation = useUpdateOrganization(effectiveOrgId)

  const zones = useMemo(() => listTimeZones(), [])

  // `userSelectedTz` is only set once the admin picks a zone. Until then the
  // displayed value tracks whatever the org record currently holds (or the
  // default when unset). This avoids setState-in-effect to sync org→state.
  const [userSelectedTz, setUserSelectedTz] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const selectedTz = userSelectedTz ?? org?.timezone ?? DEFAULT_TIMEZONE

  // Role gate mirrors BrandingSettingsPage: admin + super_admin only.
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Forbidden />
  }

  if (orgLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Organization Settings"
          description="Configure organization-wide preferences"
        />
        <div className="text-muted-foreground">Loading organization settings…</div>
      </div>
    )
  }

  function handleSave() {
    setSaveSuccess(false)
    updateMutation.mutate(
      { timezone: selectedTz },
      {
        onSuccess: () => {
          setSaveSuccess(true)
          toast.success('Organization settings saved')
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to save settings'
          toast.error(msg)
        },
      }
    )
  }

  const hasChanges = selectedTz !== (org?.timezone || DEFAULT_TIMEZONE)

  async function handleSaveTarget(target: ComplianceTarget | null) {
    await updateMutation.mutateAsync({ default_target: target })
    toast.success(
      target === null ? 'Default compliance target cleared' : 'Default compliance target saved'
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organization Settings"
        description="Configure organization-wide preferences"
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timezone
          </CardTitle>
          <CardDescription>
            The canonical timezone for this organization. Public room displays show both the
            viewer's local time and this zone when they differ. Scheduled reports and day-boundary
            calculations use this zone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {saveSuccess && (
            <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>Organization settings saved successfully!</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <TimeZonePicker value={selectedTz} onChange={setUserSelectedTz} zones={zones} />
            <Button onClick={handleSave} disabled={!hasChanges || updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Default compliance target
          </CardTitle>
          <CardDescription>
            Org-wide cleaning target inherited by every building and room unless overridden.
            Controls what the Compliance report measures for any room without its own target set.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ComplianceTargetForm
            value={org?.default_target ?? null}
            canEdit={true}
            onSave={handleSaveTarget}
          />
        </CardContent>
      </Card>
    </div>
  )
}
